// El lienzo de la oficina: la planta (dibujar.ts) y cada monigote en su silla, en píxeles nítidos (zoom entero). Se
// repinta solo si cambia la planta, la pose de alguien o quién se pinta aparte en el DOM (el que va colgando del puntero
// y los de la sala en «Ordenar»); un aviso que no cambia nada de eso no lo toca.
import { memo, useEffect, useMemo, useRef } from 'react';
import { persona, retratoDe, type Animacion } from '../../../lib/oficina/pixel-art';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { dibujarFondo, dibujarMesas } from './dibujar';
import { PUESTO, type Mundo } from './generar';
import { poseDe } from './pose';

/** Lo apagado de la escena (los de un grupo que espera a Santi): el muñeco a medias, como los que se van. */
const ALFA_APAGADO = 0.4;

/** Lo que el lienzo dibuja de cada agente. */
export interface Figura {
  semilla: string;
  animacion: Animacion;
  /** 04-11 — apagado (su grupo espera a Santi): medio transparente. */
  apagado?: boolean;
}

/** Las figuras de todos, con la misma identidad mientras nadie cambie de pose (pose.ts). */
export function useFiguras(lista: readonly ContratadoDTO[], marcas: ReadonlyMap<string, { aparcado?: boolean; apagado?: boolean }>): ReadonlyMap<string, Figura> {
  const figuras = lista.map(
    (c) => [c.agentId, { semilla: c.semillaAvatar || c.agentId, animacion: poseDe(c, !!marcas.get(c.agentId)?.aparcado), ...(marcas.get(c.agentId)?.apagado ? { apagado: true } : {}) }] as const,
  );
  const firma = JSON.stringify(figuras);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => new Map(figuras), [firma]);
}

export const Lienzo = memo(function Lienzo({ mundo, figuras, enDom }: { mundo: Mundo; figuras: ReadonlyMap<string, Figura>; enDom: ReadonlySet<string> }) {
  const lienzo = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const ctx = lienzo.current?.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    ctx.setTransform(mundo.z, 0, 0, mundo.z, 0, 0);
    dibujarFondo(ctx, mundo);
    for (const me of mundo.mesas) {
      const f = me.agentId ? figuras.get(me.agentId) : undefined;
      if (f && !enDom.has(me.agentId!)) persona(ctx, me.rect.x + me.rect.w / 2, me.rect.y + PUESTO.cabeza, retratoDe(f.semilla), f.animacion, 1, false, f.apagado ? ALFA_APAGADO : 1);
      ctx.globalAlpha = 1;
    }
    dibujarMesas(ctx, mundo);
  }, [mundo, figuras, enDom]);
  return <canvas ref={lienzo} width={mundo.ancho * mundo.z} height={mundo.alto * mundo.z} className="absolute left-0 top-0 [image-rendering:pixelated]" aria-hidden="true" />;
});
